import * as Sentry from '@sentry/react';

// Configuração do Sentry para APIs de parceiros
export const partnerSentryConfig = {
  // Captura erros de API com status 400+
  captureApiError: (error: any, context?: any) => {
    if (error.response) {
      const { status, data, config } = error.response;
      
      // Captura erros de validação (400) e outros erros de cliente/servidor
      if (status >= 400) {
        Sentry.captureException(error, {
          tags: {
            component: 'partner-api',
            endpoint: config?.url || 'unknown',
            method: config?.method || 'unknown',
            status: status.toString(),
          },
          contexts: {
            api: {
              url: config?.url,
              method: config?.method,
              status,
              statusText: error.response.statusText,
              data: data,
              headers: config?.headers,
            },
            ...context,
          },
          level: status >= 500 ? 'error' : 'warning',
        });
      }
    } else if (error.request) {
      // Erro de rede (sem resposta do servidor)
      Sentry.captureException(error, {
        tags: {
          component: 'partner-api',
          error_type: 'network_error',
        },
        contexts: {
          api: {
            url: error.config?.url,
            method: error.config?.method,
          },
          ...context,
        },
        level: 'error',
      });
    } else {
      // Erro geral
      Sentry.captureException(error, {
        tags: {
          component: 'partner-api',
          error_type: 'general_error',
        },
        contexts: {
          ...context,
        },
        level: 'error',
      });
    }
  },

  // Captura erros específicos de validação
  captureValidationError: (field: string, value: any, endpoint: string) => {
    Sentry.captureMessage(`Validation error in partner API: ${field}`, {
      level: 'warning',
      tags: {
        component: 'partner-api',
        error_type: 'validation_error',
        field,
        endpoint,
      },
      contexts: {
        validation: {
          field,
          value: typeof value === 'object' ? JSON.stringify(value) : value,
          endpoint,
        },
      },
    });
  },

  // Captura erros de autenticação
  captureAuthError: (error: any, endpoint: string) => {
    Sentry.captureException(error, {
      tags: {
        component: 'partner-api',
        error_type: 'auth_error',
        endpoint,
      },
      contexts: {
        auth: {
          endpoint,
          status: error.response?.status,
        },
      },
      level: 'warning',
    });
  },

  // Captura erros de upload de arquivos
  captureFileUploadError: (error: any, fileName: string, endpoint: string) => {
    Sentry.captureException(error, {
      tags: {
        component: 'partner-api',
        error_type: 'file_upload_error',
        endpoint,
      },
      contexts: {
        file_upload: {
          fileName,
          endpoint,
          status: error.response?.status,
          data: error.response?.data,
        },
      },
      level: 'error',
    });
  },

  // Adiciona breadcrumb para rastreamento
  addApiBreadcrumb: (url: string, method: string, status?: number) => {
    Sentry.addBreadcrumb({
      category: 'partner-api',
      message: `${method.toUpperCase()} ${url}`,
      level: status && status >= 400 ? 'error' : 'info',
      data: {
        url,
        method,
        status,
      },
    });
  },
};

// Interceptor para capturar automaticamente erros de API
export const createPartnerApiInterceptor = (axiosInstance: any) => {
  // Request interceptor para adicionar breadcrumbs
  axiosInstance.interceptors.request.use(
    (config: any) => {
      partnerSentryConfig.addApiBreadcrumb(config.url, config.method);
      return config;
    },
    (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        phase: 'request',
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor para capturar erros
  axiosInstance.interceptors.response.use(
    (response: any) => {
      partnerSentryConfig.addApiBreadcrumb(
        response.config.url,
        response.config.method,
        response.status
      );
      return response;
    },
    (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        phase: 'response',
      });
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
